/**
 * Category Card Component
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';

const THEME_COLOR = '#4ce1b6';

export const CategoryCard = ({
  category,
  isExpanded,
  selectedCount,
  selectedAmenities,
  isDark,
  onToggleExpand,
  onOpenDrawer,
  onRemoveAmenity,
}) => {
  const isMobile = isMobileShellLayout();
  const countText = selectedCount === 1 ? '1 Amenity Selected' : `${selectedCount} Amenities Selected`;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        ...(isMobile
          ? mobileAppGlassCard
          : {
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 1,
            }),
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          p: { xs: 1.25, sm: 2 },
          gap: { xs: 1, sm: 0 },
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
          },
        }}
        onClick={onToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <IconButton size="small" sx={{ mr: 1, color: 'text.secondary' }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          
          <Typography
            variant="body1"
            sx={{
              flex: 1,
              fontWeight: 500,
              color: isMobile ? '#f8fafc' : 'text.primary',
              minWidth: 0,
            }}
          >
            {category.label}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          flexWrap: 'wrap',
        }}>
        <Chip
          label={countText}
          size="small"
          sx={{
            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)',
            color: THEME_COLOR,
            fontWeight: 500,
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDrawer();
          }}
          sx={{
            backgroundColor: THEME_COLOR,
            color: '#0a0f1a',
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              backgroundColor: '#3ab89a',
            },
            textTransform: 'none',
          }}
        >
          + Add
        </Button>
        </Box>
      </Box>

      {isExpanded && selectedAmenities.length > 0 && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedAmenities.map((amenity) => (
              <Chip
                key={amenity.id}
                label={amenity.name}
                onDelete={() => onRemoveAmenity(amenity.id)}
                deleteIcon={<DeleteIcon />}
                sx={{
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                  color: THEME_COLOR,
                  '& .MuiChip-deleteIcon': {
                    color: THEME_COLOR,
                    '&:hover': {
                      color: '#3ab89a',
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

